import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { EmailDetail } from "@/lib/emails";
import { sanitizeEmailHtml } from "@/lib/sanitizeHtml";
import { cn } from "@/lib/cn";

interface FocusPaneProps {
  email: EmailDetail;
  onApprove: (body: string) => Promise<void> | void;
  onRefineClick: () => void;
  sending?: boolean;
}

/** Dominant cockpit card from the reference design. Shows the
 *  resolved sender, the case mini-timeline (entities), the
 *  AI-drafted reply (editable inline), and the two-button action
 *  row: Approve & send / Refine tone. The case timeline + the
 *  approve-and-send wiring is what makes the cockpit feel
 *  "operations cockpit, not inbox". */
export function FocusPane({
  email,
  onApprove,
  onRefineClick,
  sending,
}: FocusPaneProps) {
  const draftBody = email.draft?.body ?? "";
  const [body, setBody] = useState(draftBody);

  // If the parent swaps the email out, reset the body so the user
  // doesn't accidentally send Anand's reply to Rakesh.
  if (email.draft && body === "" && draftBody !== "") {
    setBody(draftBody);
  }

  const senderLabel =
    (email.client_name && email.client_name.trim()) ||
    email.sender_display_name ||
    "Unknown sender";

  const handleApprove = async () => {
    if (sending || !body.trim()) return;
    await onApprove(body);
  };

  return (
    <section
      data-testid="focus-pane"
      className="rounded-lg border border-op-border bg-op-surface px-5 py-4"
    >
      <header className="flex items-start justify-between gap-3 border-b border-op-border pb-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-medium text-op-primary">
            {senderLabel}
          </h2>
          <p
            className="truncate text-xs text-op-secondary"
            title={email.subject}
          >
            {email.subject || "(no subject)"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              "rounded border px-1.5 py-0.5 font-mono text-[10px]",
              email.priority === "P0"
                ? "border-bucket-need-reply-now text-bucket-need-reply-now"
                : "border-op-border text-op-secondary",
            )}
          >
            {email.priority}
          </span>
          {email.escalation_signal ? (
            <span className="rounded bg-bucket-need-reply-now/20 px-2 py-0.5 text-[10px] uppercase tracking-wider text-bucket-need-reply-now">
              high priority
            </span>
          ) : null}
        </div>
      </header>

      <CaseTimeline email={email} />
      <CaseSummary email={email} />

      <section
        data-testid="focus-pane-draft"
        className="mt-4 rounded border border-op-border bg-op-base"
      >
        <div className="flex items-center justify-between border-b border-op-border px-3 py-2">
          <span className="text-[10px] uppercase tracking-wider text-op-muted">
            AI-drafted reply
          </span>
          {email.draft?.model ? (
            <span className="font-mono text-[10px] text-op-muted">
              {email.draft.model}
            </span>
          ) : null}
        </div>
        {email.draft ? (
          <textarea
            data-testid="focus-pane-draft-body"
            className="block w-full resize-y bg-op-base px-3 py-3 text-sm text-op-primary outline-none placeholder:text-op-muted"
            rows={9}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck="true"
          />
        ) : (
          <p className="px-3 py-6 text-sm text-op-muted">
            No draft yet — Sonnet only writes a draft when the triage
            says a reply is needed.
          </p>
        )}
      </section>

      <footer
        data-testid="focus-pane-actions"
        className="mt-4 flex flex-col gap-2 sm:flex-row"
      >
        <Button
          data-testid="focus-pane-approve"
          variant="default"
          className="flex-1 bg-op-accent text-op-primary hover:bg-op-accent/90"
          onClick={handleApprove}
          disabled={!email.draft || !body.trim() || !!sending}
        >
          {sending ? "Sending…" : "Approve & send"}
        </Button>
        <Button
          data-testid="focus-pane-refine"
          variant="outline"
          className="flex-1"
          onClick={onRefineClick}
          disabled={!email.draft || !!sending}
        >
          Refine tone
        </Button>
      </footer>

      <BodyExcerpt email={email} />
    </section>
  );
}

function CaseTimeline({ email }: { email: EmailDetail }) {
  // Phase 7: until the case API surfaces the full timeline (Phase 8),
  // synthesize a three-step strip from the triage signals so the
  // operator can see at a glance where the case stands.
  const steps = synthesizeTimelineSteps(email);
  return (
    <ol
      data-testid="focus-pane-timeline"
      className="mt-3 flex flex-wrap gap-2 text-[11px] text-op-secondary"
    >
      {steps.map((step) => (
        <li
          key={step.label}
          className={cn(
            "rounded border px-2 py-0.5 font-mono uppercase tracking-wider",
            step.reached
              ? "border-op-border-accent text-op-accent"
              : "border-op-border text-op-muted",
          )}
        >
          {step.label}
        </li>
      ))}
    </ol>
  );
}

function synthesizeTimelineSteps(email: EmailDetail) {
  const state = email.triage_state;
  const lookup = {
    received: true,
    triaged: state !== "NEW",
    drafted: ["DRAFTED", "ESCALATED", "AWAITING_REPLY", "DONE"].includes(state),
    sent: ["AWAITING_REPLY", "DONE"].includes(state),
  };
  return [
    { label: "received", reached: lookup.received },
    { label: "triaged", reached: lookup.triaged },
    { label: "drafted", reached: lookup.drafted },
    { label: "sent", reached: lookup.sent },
  ];
}

function CaseSummary({ email }: { email: EmailDetail }) {
  if (!email.summary_line_1 && !email.summary_line_2) return null;
  return (
    <section
      data-testid="focus-pane-summary"
      className="mt-3 rounded border border-op-border bg-op-base px-3 py-2"
    >
      {email.summary_line_1 ? (
        <p className="text-xs text-op-primary">{email.summary_line_1}</p>
      ) : null}
      {email.summary_line_2 ? (
        <p className="text-[11px] text-op-secondary">{email.summary_line_2}</p>
      ) : null}
    </section>
  );
}

function BodyExcerpt({ email }: { email: EmailDetail }) {
  const html = email.body_html
    ? sanitizeEmailHtml(email.body_html)
    : null;
  return (
    <details
      data-testid="focus-pane-body"
      className="mt-4 rounded border border-op-border bg-op-base"
    >
      <summary className="cursor-pointer px-3 py-2 text-[10px] uppercase tracking-wider text-op-muted">
        Original message
      </summary>
      {html ? (
        <div
          className="prose prose-invert max-w-none px-3 py-3 text-sm text-op-primary [&_a]:text-op-accent [&_img]:max-w-full"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="whitespace-pre-wrap break-words px-3 py-3 font-sans text-sm text-op-primary">
          {email.body_text || "(empty message)"}
        </pre>
      )}
    </details>
  );
}
